// High-Confidence Board — the FPE conviction surface. Ranks football match-winner
// picks by how strong a STRIKZA *read* they are (0-100), NOT by raw win probability,
// so obvious chalk does not dominate and the engine's confident calls — including
// flagged upsets — sit on top. Reads get_high_confidence_board (outputs-only).

import { useMemo } from "react";
import { useHighConfidenceBoard, type HighConfidencePick } from "./useHighConfidenceBoard.ts";

const BANDS: ReadonlyArray<{ key: "HIGH" | "MEDIUM" | "LOW"; label: string; hint: string }> = [
  { key: "HIGH", label: "High confidence", hint: "Strongest reads — the engine rates these materially above the field." },
  { key: "MEDIUM", label: "Medium confidence", hint: "A real model lean with an edge, but less conviction." },
  { key: "LOW", label: "Low confidence", hint: "Market-aligned — we hold no independent edge here." },
];

function fmtOdds(o: number | null): string {
  return o != null && Number.isFinite(o) ? o.toFixed(2) : "—";
}

function fmtKick(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function bandColor(band: string): string {
  return band === "HIGH" ? "var(--accent, #e8b54a)" : band === "MEDIUM" ? "var(--text, #ddd)" : "var(--text-muted, #888)";
}

function PickRow({ p }: { p: HighConfidencePick }): JSX.Element {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "44px 1fr auto",
        gap: 14,
        alignItems: "center",
        padding: "12px 14px",
        borderTop: "1px solid var(--border, rgba(255,255,255,0.06))",
      }}
    >
      {/* Confidence score */}
      <div style={{ textAlign: "center" }}>
        <div className="mono" style={{ fontSize: 20, fontWeight: 700, color: bandColor(p.band), lineHeight: 1 }}>
          {p.confidence}
        </div>
        <div className="mono" style={{ fontSize: 8, letterSpacing: "0.12em", color: "var(--text-muted)" }}>CONF</div>
      </div>

      {/* Fixture + pick */}
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span
            className="mono"
            style={{
              fontSize: 9, letterSpacing: "0.08em", padding: "2px 6px", borderRadius: 4,
              color: "var(--text-muted)", border: "1px solid var(--border, rgba(255,255,255,0.12))",
            }}
          >
            {p.market}
          </span>
          <span style={{ fontWeight: 600 }}>{p.selection}</span>
          {p.is_upset ? (
            <span
              className="mono"
              style={{
                fontSize: 9, letterSpacing: "0.1em", padding: "2px 6px", borderRadius: 4,
                color: "#1a1a1a", background: "var(--accent, #e8b54a)", fontWeight: 700,
              }}
            >
              UPSET
            </span>
          ) : null}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
          {p.home_team} v {p.away_team}
          {p.competition ? ` · ${p.competition}` : ""} · {fmtKick(p.kickoff_at)}
        </div>
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>{p.note}</div>
      </div>

      {/* Fair odds */}
      <div style={{ textAlign: "right" }}>
        <div className="mono" style={{ fontSize: 16, fontWeight: 600 }}>{fmtOdds(p.fair_odds)}</div>
        <div className="mono" style={{ fontSize: 8, letterSpacing: "0.12em", color: "var(--text-muted)" }}>FAIR</div>
      </div>
    </div>
  );
}

// Pure presentational view (no data fetch) — testable with renderToString.
export function HighConfidenceBoardView(
  { picks, loading, error }: { picks: HighConfidencePick[]; loading: boolean; error: string | null },
): JSX.Element {
  const grouped = useMemo(() => {
    const g: Record<"HIGH" | "MEDIUM" | "LOW", HighConfidencePick[]> = { HIGH: [], MEDIUM: [], LOW: [] };
    for (const p of picks) g[p.band].push(p);
    return g;
  }, [picks]);

  const upsetCount = useMemo(() => picks.filter((p) => p.is_upset).length, [picks]);

  return (
    <div className="page" data-screen-label="High-Confidence Board">
      <div className="ex-head">
        <div>
          <h1>High-Confidence Board</h1>
          <div className="sub">
            Ranked by STRIKZA conviction — how strong a read it is, not how obvious the favourite.
            The engine&rsquo;s most confident calls sit on top; market-aligned chalk falls below.
          </div>
        </div>
        <div className="pill-row">
          <span className="tag">Result · Goals · BTTS</span>
          <span className="tag">Conviction-ranked</span>
          {upsetCount > 0 ? <span className="tag phantom"><span className="dot" />{upsetCount} upset{upsetCount === 1 ? "" : "s"} flagged</span> : null}
        </div>
      </div>

      {loading ? (
        <div className="sub" style={{ padding: "24px 0" }}>Loading the board…</div>
      ) : error ? (
        <div className="sub" style={{ padding: "24px 0", color: "var(--danger, #e06)" }}>
          Couldn&rsquo;t load the board: {error}
        </div>
      ) : picks.length === 0 ? (
        <div className="sub" style={{ padding: "24px 0" }}>
          No priced fixtures in the window yet — the board fills as the engine prices upcoming matches.
        </div>
      ) : (
        BANDS.map(({ key, label, hint }) => {
          const rows = grouped[key] ?? [];
          if (rows.length === 0) return null;
          return (
            <section key={key} style={{ marginTop: 22 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 2 }}>
                <h2 style={{ fontSize: 14, letterSpacing: "0.04em", margin: 0 }}>{label}</h2>
                <span className="mono" style={{ fontSize: 10, color: "var(--text-muted)" }}>{rows.length}</span>
              </div>
              <div className="sub" style={{ fontSize: 11, marginBottom: 8 }}>{hint}</div>
              <div style={{ border: "1px solid var(--border, rgba(255,255,255,0.06))", borderRadius: 8, overflow: "hidden" }}>
                {rows.map((p) => <PickRow key={p.fixture_id} p={p} />)}
              </div>
            </section>
          );
        })
      )}
    </div>
  );
}

export function HighConfidenceBoardPage(): JSX.Element {
  const { picks, loading, error } = useHighConfidenceBoard();
  return <HighConfidenceBoardView picks={picks} loading={loading} error={error} />;
}
